import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { db, storage } from '../../firebase';
import { doc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { updateProfile } from 'firebase/auth';
import { EmailAuthProvider, reauthenticateWithCredential, updatePassword } from 'firebase/auth';
import { auth } from '../../firebase';
import {
  Loader2, Save, CheckCircle2, User, Lock, AlertCircle, Globe,
  Phone, AlignLeft, Briefcase, Link2, MessageCircle, Hash, Camera, MonitorPlay, PenTool
} from 'lucide-react';
import PasswordInput from '../../components/auth/PasswordInput';
import PhoneInput, { ARAB_COUNTRIES } from '../../components/ui/PhoneInput';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWhatsapp, faXTwitter, faLinkedinIn, faInstagram, faFacebookF, faYoutube, faBehance } from '@fortawesome/free-brands-svg-icons';

const SOCIAL_PLATFORMS = [
  { key: 'whatsapp', label: 'واتساب', placeholder: 'https://wa.me/967XXXXXXXXX', icon: faWhatsapp },
  { key: 'linkedin', label: 'لينكد إن', placeholder: 'https://linkedin.com/in/username', icon: faLinkedinIn },
  { key: 'instagram', label: 'إنستغرام', placeholder: 'https://instagram.com/username', icon: faInstagram },
  { key: 'facebook', label: 'فيسبوك', placeholder: 'https://facebook.com/username', icon: faFacebookF },
  { key: 'behance', label: 'بيهانس', placeholder: 'https://behance.net/username', icon: faBehance },
];

export default function InstructorSettings() {
  const { currentUser, userData, refreshUserData } = useAuth();

  // Active tab
  const [activeTab, setActiveTab] = useState('profile');

  // Profile form
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [form, setForm] = useState({
    name: '', username: '', phone: '', countryCode: '+967', specialization: '', bio: '',
    whatsapp: '', waCode: '+967', twitter: '', linkedin: '', instagram: '',
    facebook: '', behance: ''
  });

  // Avatar
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [avatarFile, setAvatarFile] = useState(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  // Password
  const [pwForm, setPwForm] = useState({ current: '', newPw: '', confirm: '', isNewValid: false });
  const [pwSaving, setPwSaving] = useState(false);
  const [pwError, setPwError] = useState('');
  const [pwSuccess, setPwSuccess] = useState(false);

  // Load existing data from Firestore
  useEffect(() => {
    if (userData) {
      const social = userData.instructorData?.social || {};
      let parsedWaCode = '+967';
      let parsedWaPhone = '';
      if (social.whatsapp && social.whatsapp.includes('wa.me/')) {
        let rawNum = social.whatsapp.split('wa.me/')[1].replace(/\D/g, '');
        const match = ARAB_COUNTRIES.find(c => rawNum.startsWith(c.code.replace('+', '')));
        if (match) {
           parsedWaCode = match.code;
           parsedWaPhone = rawNum.slice(match.code.replace('+', '').length);
        } else {
           parsedWaPhone = rawNum;
        }
      } else if (social.whatsapp) {
        parsedWaPhone = social.whatsapp.replace(/\D/g, '');
      }

      setForm({
        name: userData.name || '',
        username: userData.username || '',
        phone: userData.phone?.replace(/^\+\d+\s?/, '') || userData.phone || '',
        countryCode: (userData.phone && userData.phone.startsWith('+')) ? (userData.phone.split(' ')[0] || '+967') : '+967',
        specialization: userData.instructorData?.specialization || '',
        bio: userData.instructorData?.bio || '',
        whatsapp: parsedWaPhone,
        waCode: parsedWaCode,
        twitter: social.twitter || '',
        linkedin: social.linkedin || '',
        instagram: social.instagram || '',
        facebook: social.facebook || '',
        behance: social.behance || '',
      });
      if (userData.photoURL) {
        setAvatarPreview(userData.photoURL);
      }
    }
  }, [userData]);

  // Handle avatar file selection
  const handleAvatarSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      alert('حجم الصورة كبير جداً. الحد الأقصى هو 2 ميغابايت.');
      return;
    }
    setAvatarFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setAvatarPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  // Save profile + avatar + social links
  const handleSaveProfile = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.specialization.trim()) {
      alert('يرجى تعبئة الاسم والتخصص على الأقل.');
      return;
    }
    setIsSaving(true);
    setSaveSuccess(false);

    try {
      if (form.username.trim() && form.username.trim() !== (userData.username || '')) {
        const usernameQuery = query(collection(db, 'users'), where('username', '==', form.username.trim().toLowerCase()));
        const snap = await getDocs(usernameQuery);
        if (!snap.empty && snap.docs[0].id !== currentUser.uid) {
          alert('عذراً، الرابط المخصص هذا محجوز مسبقاً، يرجى اختيار اسم آخر.');
          setIsSaving(false);
          return;
        }
      }

      let finalAvatarUrl = avatarPreview;

      // Upload avatar to Firebase Storage if a new file was selected
      if (avatarFile) {
        setUploadingAvatar(true);
        const storageRef = ref(storage, `instructors/${currentUser.uid}/avatar`);
        await uploadBytes(storageRef, avatarFile);
        finalAvatarUrl = await getDownloadURL(storageRef);

        // Update Firebase Auth profile
        await updateProfile(auth.currentUser, { photoURL: finalAvatarUrl });
        setUploadingAvatar(false);
      }

      // Build update payload
      const updatePayload = {
        name: form.name.trim(),
        username: form.username.trim().toLowerCase(),
        phone: `${form.countryCode} ${form.phone.trim()}`,
        'instructorData.specialization': form.specialization.trim(),
        'instructorData.bio': form.bio.trim(),
        'instructorData.social': {
          whatsapp: form.whatsapp.trim() ? `https://wa.me/${form.waCode.replace('+','')}${form.whatsapp.trim()}` : '',
          twitter: form.twitter || '',
          linkedin: form.linkedin.trim(),
          instagram: form.instagram.trim(),
          facebook: form.facebook.trim(),
          behance: form.behance.trim(),
        },
      };

      // Only update photoURL if we have a new one
      if (finalAvatarUrl && finalAvatarUrl !== userData?.photoURL) {
        updatePayload.photoURL = finalAvatarUrl;
      }

      await updateDoc(doc(db, 'users', currentUser.uid), updatePayload);
      await refreshUserData();

      if (avatarFile) {
        setAvatarPreview(finalAvatarUrl);
        setAvatarFile(null);
      }

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error(err);
      alert('حدث خطأ أثناء حفظ الإعدادات');
    } finally {
      setIsSaving(false);
      setUploadingAvatar(false);
    }
  };

  // Change password
  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPwError('');
    setPwSuccess(false);

    if (pwForm.newPw.length < 6) {
      setPwError('كلمة السر الجديدة يجب أن تكون 6 أحرف على الأقل');
      return;
    }
    if (pwForm.newPw !== pwForm.confirm) {
      setPwError('كلمة السر الجديدة وتأكيدها غير متطابقتين');
      return;
    }

    setPwSaving(true);
    try {
      const credential = EmailAuthProvider.credential(currentUser.email, pwForm.current);
      await reauthenticateWithCredential(currentUser, credential);
      await updatePassword(currentUser, pwForm.newPw);
      setPwSuccess(true);
      setPwForm({ current: '', newPw: '', confirm: '', isNewValid: false });
      setTimeout(() => setPwSuccess(false), 3000);
    } catch (err) {
      if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        setPwError('كلمة السر الحالية غير صحيحة');
      } else {
        setPwError('حدث خطأ أثناء تغيير كلمة السر');
      }
    } finally {
      setPwSaving(false);
    }
  };

  const inputCls = "w-full pl-4 pr-4 py-3.5 bg-white dark:bg-zinc-900 border border-slate-200 dark:border-white/10 rounded-xl outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 transition-all font-medium text-slate-900 dark:text-white shadow-sm hover:bg-slate-50 dark:hover:bg-white/5";

  // ─── Profile Tab ─────────────────────────────────────────────
  const renderProfileTab = () => (
    <form onSubmit={handleSaveProfile} className="bg-white/70 dark:bg-zinc-900/50 backdrop-blur-2xl border border-slate-200/50 dark:border-white/5 rounded-2xl p-8 shadow-xl space-y-8 animate-in fade-in duration-500">

      {/* Avatar Upload */}
      <div className="flex flex-col sm:flex-row items-center gap-6 pb-8 border-b border-slate-200 dark:border-white/10">
        <div className="relative group">
          <div className="w-28 h-28 rounded-2xl border-4 border-white dark:border-zinc-800 shadow-xl overflow-hidden bg-slate-100 dark:bg-zinc-800 flex items-center justify-center relative">
            {avatarPreview ? (
              <img src={avatarPreview} alt="Avatar" className="w-full h-full object-cover" />
            ) : (
              <User className="w-12 h-12 text-slate-300 dark:text-zinc-600" />
            )}
            <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
              <Camera className="w-8 h-8 text-white" />
            </div>
          </div>

          <input
            type="file"
            accept="image/*"
            onChange={handleAvatarSelect}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
          />
          {uploadingAvatar && (
            <div className="absolute inset-0 bg-white/80 dark:bg-black/80 rounded-2xl flex items-center justify-center z-20">
              <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
            </div>
          )}
          <div className="absolute -bottom-2 -right-2 bg-amber-400 text-amber-900 p-1.5 rounded-full shadow-lg z-10 pointer-events-none ring-4 ring-white dark:ring-zinc-900 border border-amber-300">
            <Camera className="w-4 h-4" />
          </div>
        </div>
        <div className="text-center sm:text-right">
          <h3 className="font-bold text-slate-900 dark:text-white text-lg">الصورة الشخصية</h3>
          <p className="text-slate-500 dark:text-zinc-400 text-sm mt-1">يُفضل رفع صورة مربعة واضحة بحجم أقصاه 2MB.</p>
          {avatarFile && <span className="text-sm font-bold text-amber-500 mt-2 block">سيتم تحديث الصورة عند الضغط على "حفظ التعديلات"</span>}
        </div>
      </div>

      {/* Basic Info */}
      <div>
        <h3 className="text-xl font-black text-slate-900 dark:text-white flex items-center gap-3 mb-6">
          <div className="p-2.5 bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400 rounded-xl">
            <User className="w-5 h-5" />
          </div>
          البيانات الأساسية
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-bold text-slate-700 dark:text-zinc-300 mb-2">الاسم بالكامل <span className="text-rose-500">*</span></label>
            <input type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className={inputCls} required />
          </div>
          <div>
            <PhoneInput
              phoneValue={form.phone}
              onPhoneChange={e => setForm({ ...form, phone: e.target.value.replace(/[^0-9]/g, '') })}
              countryCodeValue={form.countryCode}
              onCountryCodeChange={code => setForm({ ...form, countryCode: code })}
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-bold text-slate-700 dark:text-zinc-300 mb-2 flex items-center gap-1.5"><Globe className="w-4 h-4 text-slate-400" /> رابط الصفحة المخصص (اختياري)</label>
            <div className="flex bg-white/50 dark:bg-black/20 border border-slate-200 dark:border-white/10 rounded-xl overflow-hidden focus-within:border-amber-500 focus-within:ring-2 focus-within:ring-amber-500/20 transition-all">
              <span className="px-4 py-3 bg-slate-100 dark:bg-white/5 border-l border-slate-200 dark:border-white/10 text-slate-500 dark:text-zinc-400 font-bold" dir="ltr">
                lobabschool.com/instructor/
              </span>
              <input 
                type="text" 
                value={form.username} 
                onChange={e => setForm({ ...form, username: e.target.value.replace(/[^a-zA-Z0-9_-]/g, '') })} 
                className="flex-1 px-4 py-3 bg-transparent outline-none font-bold text-slate-900 dark:text-white" 
                placeholder="mraseel"
                dir="ltr"
              />
            </div>
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-bold text-slate-700 dark:text-zinc-300 mb-2 flex items-center gap-1.5">
              <Briefcase className="w-4 h-4 text-slate-400" /> التخصص الدقيق <span className="text-rose-500">*</span>
            </label>
            <input type="text" value={form.specialization} onChange={e => setForm({ ...form, specialization: e.target.value })} className={inputCls} placeholder="مثال: مصمم واجهات وتجربة مستخدم (UI/UX)" required />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-bold text-slate-700 dark:text-zinc-300 mb-2 flex items-center gap-1.5">
              <AlignLeft className="w-4 h-4 text-slate-400" /> نبذة شخصية احترافية
            </label>
            <textarea value={form.bio} onChange={e => setForm({ ...form, bio: e.target.value })} className={inputCls} rows={4} placeholder="تحدث عن خبرتك ومسيرتك... سيتم عرضها في صفحتك العامة" />
          </div>
        </div>
      </div>

      {/* Social Links */}
      <div className="pt-6 border-t border-slate-100 dark:border-white/5">
        <h3 className="text-xl font-black text-slate-900 dark:text-white flex items-center gap-3 mb-2">
          <div className="p-2.5 bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 rounded-xl">
            <Link2 className="w-5 h-5" />
          </div>
          روابط منصات التواصل
        </h3>
        <p className="text-sm text-slate-500 dark:text-zinc-400 font-medium mb-6">سيتم عرض الأيقونات في صفحتك العامة. اترك الحقل فارغاً لإخفاء المنصة.</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {SOCIAL_PLATFORMS.map(platform => (
            <div key={platform.key} className="relative">
              <label className="flex items-center gap-2 text-sm font-bold text-slate-700 dark:text-zinc-300 mb-2">
                <div className="w-8 h-8 rounded-lg bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 flex items-center justify-center text-slate-500 dark:text-zinc-400 shrink-0">
                  <FontAwesomeIcon icon={platform.icon} className="w-4 h-4" />
                </div>
                {platform.label}
              </label>
              {platform.key === 'whatsapp' ? (
                <PhoneInput
                  phoneValue={form.whatsapp}
                  onPhoneChange={e => setForm({ ...form, whatsapp: e.target.value.replace(/[^0-9]/g, '') })}
                  countryCodeValue={form.waCode}
                  onCountryCodeChange={code => setForm({ ...form, waCode: code })}
                  required={false}
                />
              ) : (
                <input
                  type="url"
                  value={form[platform.key]}
                  onChange={e => setForm({ ...form, [platform.key]: e.target.value })}
                  className={inputCls}
                  placeholder={platform.placeholder}
                  dir="ltr"
                />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Save Button */}
      <div className="pt-6 flex items-center justify-end gap-4">
        {saveSuccess && (
          <span className="flex items-center gap-2 text-emerald-600 font-bold animate-in fade-in">
            <CheckCircle2 className="w-5 h-5" /> تم الحفظ بنجاح
          </span>
        )}
        <button
          type="submit"
          disabled={isSaving}
          className="bg-amber-600 hover:bg-amber-700 text-white px-8 py-3.5 rounded-xl font-bold flex items-center gap-2 shadow-lg shadow-amber-500/20 transition-all hover:-translate-y-0.5 disabled:opacity-50"
        >
          {isSaving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
          حفظ التعديلات
        </button>
      </div>
    </form>
  );

  // ─── Security Tab ────────────────────────────────────────────
  const renderSecurityTab = () => (
    <form onSubmit={handleChangePassword} className="bg-white/70 dark:bg-zinc-900/50 backdrop-blur-2xl border border-slate-200/50 dark:border-white/5 rounded-2xl p-8 shadow-xl space-y-8 animate-in fade-in duration-500">
      <h3 className="text-xl font-black text-slate-900 dark:text-white flex items-center gap-3 mb-2">
        <div className="p-2.5 bg-rose-50 dark:bg-rose-500/10 text-rose-600 dark:text-rose-400 rounded-xl">
          <Lock className="w-5 h-5" />
        </div>
        كلمة السر والأمان
      </h3>

      <div className="bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20 text-emerald-700 dark:text-emerald-400 p-4 rounded-xl flex items-center gap-3">
        <CheckCircle2 className="w-6 h-6 shrink-0" />
        <div>
          <p className="font-bold">حالة الأمان ممتاز</p>
          <p className="text-sm font-medium opacity-80">حسابك محمي وموثق بشكل آمن.</p>
        </div>
      </div>

      {pwError && (
        <div className="bg-rose-50 dark:bg-rose-500/10 border border-rose-200 dark:border-rose-500/20 text-rose-600 dark:text-rose-400 px-5 py-4 rounded-xl text-sm font-bold flex items-center gap-3 animate-in fade-in">
          <AlertCircle className="w-5 h-5 shrink-0" /> {pwError}
        </div>
      )}
      {pwSuccess && (
        <div className="bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20 text-emerald-600 dark:text-emerald-400 px-5 py-4 rounded-xl text-sm font-bold flex items-center gap-3 animate-in fade-in">
          <CheckCircle2 className="w-5 h-5 shrink-0" /> تم تغيير كلمة السر بنجاح!
        </div>
      )}

      <div className="space-y-6">
        <div>
          <label className="block text-sm font-bold text-slate-700 dark:text-zinc-300 mb-2">كلمة السر الحالية</label>
          <PasswordInput
            id="instructor-current-pw"
            value={pwForm.current}
            onChange={e => setPwForm({ ...pwForm, current: e.target.value })}
            showValidator={false}
            className="px-5 py-4 bg-white/50 dark:bg-black/20"
            placeholder="أدخل كلمة السر الحالية"
          />
        </div>
        <div>
          <label className="block text-sm font-bold text-slate-700 dark:text-zinc-300 mb-2">كلمة السر الجديدة</label>
          <PasswordInput
            id="instructor-new-pw"
            value={pwForm.newPw}
            onChange={e => setPwForm({ ...pwForm, newPw: e.target.value })}
            onValidityChange={(valid) => setPwForm(prev => ({ ...prev, isNewValid: valid }))}
            className="px-5 py-4 bg-white/50 dark:bg-black/20"
            placeholder="أدخل كلمة سر قوية"
          />
        </div>
        <div>
          <label className="block text-sm font-bold text-slate-700 dark:text-zinc-300 mb-2">تأكيد كلمة السر الجديدة</label>
          <PasswordInput
            id="instructor-confirm-pw"
            value={pwForm.confirm}
            onChange={e => setPwForm({ ...pwForm, confirm: e.target.value })}
            showValidator={false}
            className="px-5 py-4 bg-white/50 dark:bg-black/20"
            placeholder="أعد إدخال كلمة السر"
          />
        </div>
      </div>

      <button
        type="submit"
        disabled={pwSaving || (pwForm.newPw.length > 0 && !pwForm.isNewValid)}
        className="w-full flex items-center justify-center gap-2 bg-slate-900 dark:bg-white text-white dark:text-slate-900 hover:bg-slate-800 dark:hover:bg-slate-200 py-4 rounded-xl font-bold text-lg transition-all shadow-lg disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {pwSaving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Lock className="w-5 h-5" />}
        {pwSaving ? 'جاري التحقق...' : 'تحديث كلمة السر'}
      </button>
    </form>
  );

  return (
    <div className="max-w-6xl mx-auto space-y-6 animate-in fade-in duration-700 relative">
      {/* Ambient Glow */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-amber-500/15 dark:bg-amber-500/5 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/3 pointer-events-none"></div>

      <div className="relative z-10 mb-8">
        <h1 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight mb-2">إعدادات الحساب</h1>
        <p className="text-slate-500 dark:text-zinc-400 font-medium">قم بإدارة ملفك الشخصي وبياناتك الأمنية وروابط التواصل.</p>
      </div>

      <div className="flex flex-col lg:flex-row gap-8 relative z-10">

        {/* Sidebar Tabs */}
        <div className="lg:w-72 shrink-0">
          <div className="bg-white/80 dark:bg-zinc-900/60 backdrop-blur-3xl border border-slate-200/50 dark:border-white/10 rounded-2xl p-4 shadow-xl sticky top-24 space-y-2">
            {[
              { id: 'profile', icon: User, label: 'الملف الشخصي' },
              { id: 'security', icon: Lock, label: 'كلمة السر والأمان' },
            ].map(tab => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center gap-4 px-5 py-4 rounded-xl font-bold transition-all duration-300 outline-none ${isActive
                    ? 'bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400 shadow-sm border border-amber-100 dark:border-amber-500/20'
                    : 'bg-transparent text-slate-600 dark:text-zinc-400 hover:bg-slate-50 dark:hover:bg-white/5 hover:text-slate-900 dark:hover:text-white border border-transparent'
                    }`}
                >
                  <Icon className={`w-5 h-5 transition-transform duration-300 ${isActive ? 'scale-110' : ''}`} />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {activeTab === 'profile' && renderProfileTab()}
          {activeTab === 'security' && renderSecurityTab()}
        </div>

      </div>
    </div>
  );
}
